import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { List, X, Phone } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const navLinks = [
    { id: "hero", label: "Startseite", type: "scroll" },
    { id: "leistungen", label: "Leistungen", type: "scroll" },
    { id: "/treppen", label: "Treppen", type: "link" },
    { id: "/mikrozement", label: "Mikrozement", type: "link" },
    { id: "ueber-uns", label: "Über Uns", type: "scroll" },
    { id: "kontakt", label: "Kontakt", type: "scroll" },
  ];

  const scrollToSection = (sectionId) => {
    // If not on home page, navigate first
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
    setMobileMenuOpen(false);
  };

  const navigateToPage = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const scrollToContact = () => {
    scrollToSection("kontakt");
  };

  return (
    <header className="bg-white border-b border-slate-200 sticky top-0 z-50" data-testid="header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo Text */}
          <button 
            onClick={() => scrollToSection("hero")} 
            className="flex items-center"
            data-testid="logo-link"
          >
            <span className="font-heading text-2xl md:text-3xl font-black uppercase tracking-tight">
              <span className="text-slate-900">PURE</span>
              <span className="bg-gradient-to-r from-amber-400 via-yellow-500 to-amber-600 bg-clip-text text-transparent ml-1">EPOX</span>
            </span>
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              link.type === "link" ? (
                <Link
                  key={link.id}
                  to={link.id}
                  className={`font-body font-medium transition-colors ${
                    location.pathname === link.id
                      ? "text-sky-700"
                      : "text-slate-600 hover:text-slate-900"
                  }`}
                  data-testid={`nav-link-${link.id.replace("/", "")}`}
                >
                  {link.label}
                </Link>
              ) : (
                <button
                  key={link.id}
                  onClick={() => scrollToSection(link.id)}
                  className="font-body font-medium text-slate-600 hover:text-slate-900 transition-colors"
                  data-testid={`nav-link-${link.id}`}
                >
                  {link.label}
                </button>
              )
            ))}
            <button
              onClick={() => navigateToPage("/probeflaeche")}
              className={`font-body font-medium transition-colors ${
                location.pathname === "/probeflaeche"
                  ? "text-sky-700"
                  : "text-slate-600 hover:text-slate-900"
              }`}
              data-testid="nav-link-probeflaeche"
            >
              Probefläche
            </button>
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center space-x-4">
            <a href="tel:+4916092653278" className="flex items-center text-slate-600 hover:text-slate-900 font-medium">
              <Phone size={20} weight="bold" className="mr-2 text-sky-700" />
              <span className="hidden lg:inline">+49 160 92653278</span>
            </a>
            <Button 
              onClick={scrollToContact}
              className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm font-semibold uppercase tracking-wide px-6 py-5 h-auto"
              data-testid="header-cta-button"
            >
              Anfrage stellen
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-slate-900"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
            aria-label="Menu öffnen"
          >
            {mobileMenuOpen ? <X size={28} /> : <List size={28} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav className="md:hidden py-4 border-t border-slate-200" data-testid="mobile-nav">
            <div className="flex flex-col space-y-4">
              {navLinks.map((link) => (
                link.type === "link" ? (
                  <Link
                    key={link.id}
                    to={link.id}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`font-body font-medium py-2 text-left ${
                      location.pathname === link.id
                        ? "text-sky-700"
                        : "text-slate-600"
                    }`}
                    data-testid={`mobile-nav-link-${link.id.replace("/", "")}`}
                  >
                    {link.label}
                  </Link>
                ) : (
                  <button
                    key={link.id}
                    onClick={() => scrollToSection(link.id)}
                    className="font-body font-medium py-2 text-slate-600 text-left"
                    data-testid={`mobile-nav-link-${link.id}`}
                  >
                    {link.label}
                  </button>
                )
              ))}
              <button
                onClick={() => navigateToPage("/probeflaeche")}
                className={`font-body font-medium py-2 text-left ${
                  location.pathname === "/probeflaeche"
                    ? "text-sky-700"
                    : "text-slate-600"
                }`}
                data-testid="mobile-nav-link-probeflaeche"
              >
                Probefläche
              </button>
              <Button 
                onClick={scrollToContact}
                className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-sm font-semibold uppercase tracking-wide px-6 py-4 h-auto mt-2"
                data-testid="mobile-cta-button"
              >
                Anfrage stellen
              </Button>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
